import Api from './API';
import { SearchParamsT } from '../components/reports/ReportDetailDep';
import {
  DataGridI,
  GeneralDivision,
  LeaveTypesI,
  LocaleAttachmentsType,
  LocaleTemplateType,
  MembersDataI,
  MenuItemsI,
  RemaniningDays,
  AccessItemI,
  SelectableDivisions,
  SpecialDay,
  UserLocationI,
  LeaveRequestId,
  EmployeesPlanerI,
  PlannerListDTO,
  RequestsI,
  ProjectReportRes,
  LeaveTemplatesType,
  AbsenceI,
  AbsenceListItemI,
  PlannerListMap,
  AbsentEmployeesT,
  UnionLocations,
  FiltersPages,
  UserFilters,
  UserList,
  EditCompensatoryOff,
  SystemState,
  LeavesAll,
  EmployeeEventsResponse,
  SelectableDivisionsListItem,
  DownloadTemplate,
  ActingRole,
  PlannerFilterDto,
  ReportDepStateDto,
  OrgReportSettings, OrgReportSettingsUpdateDto, HrReportDto, HrReportResponse, OrgReportPayload,
  CalendarSettingsType,
  FeatureSwitcherSelectorsType,
  SysAdminAPIsResponse,
  LeaveRequestPrerequisites, AbsenceSettingsI, AbsenceSettingsUpdateBodyI
} from '../types';
import { Col, ReportOrgFilters, TableDataT } from '../components/reports/ReportDetailOrg';
import { Project } from '../components/reports/ReportProject';
import {
  SettingsCoefficientType,
  SettingsEmployeeListT, UpdateEmployeeBody,
} from '../store/Settings';
import { AnalyticsT } from '../store/Analytics';
import { DaysCountRange } from '../components/requestModal/types';
import { ApplicationTemplatesGrid, CountryList, TypesForTemplates } from '../store/ApplicationTemplates';
import { dateToYMDFormat } from '../utils/dateHelper';
import { GlobalSearchCacheResult } from '../store/GlobalSearch';
import { DaysAvailableNamespace } from '../components/daysAvailable/settings/types';
import { SavedSettingsType } from '../store/Settings';

const prefix = `api/${process.env.REACT_APP_API_VERSION}`;
const prefixV2 = `api/${process.env.REACT_APP_API_VERSION_2}`;

function prepareQueryString( obj: Record<string, string | number | undefined>):string {
  const params = new URLSearchParams();
  for (const prop in obj) {
    if (obj[prop] !== undefined) {
      params.append(prop, (obj[prop] as string|number).toString());
    }
  }

  return params.toString();
}

const specialDaysService = {
  path: process.env.REACT_APP_DAYS_API_URL,
  getList: function(payload: string){
    return Api<string,SpecialDay[]>().get(`${this.path}/${prefix}/specialdays/list-period?${payload}`);
  }
};

const calendarService = {
  path:  process.env.REACT_APP_DAYS_API_URL,
  createEvent: function(location: string, body: Omit<SpecialDay, 'id'>){
    const params = prepareQueryString({
      country: location,
    });
    return Api<Omit<SpecialDay, 'id'>, SpecialDay>().post(`${this.path}/${prefix}/specialdays/create?${params}`, body);
  },
  deleteEvent: function(id:number){
    const params = prepareQueryString({
      id,
    });
    return Api<Pick<SpecialDay, 'id'>, SpecialDay>().delete(`${this.path}/${prefix}/specialdays/delete?${params}`);
  },
  editEvent: function(location: string, body: Omit<SpecialDay, 'id'>){
    const params = prepareQueryString({
      country: location,
    });
    return Api<Omit<SpecialDay, 'id'>, SpecialDay>().post(`${this.path}/${prefix}/specialdays/update?${params}`, body);
  },
  daysCount: function(data: {startDate: string, endDate: string, userId?: number, country?: UnionLocations, leaveRequestType: number}){
    const {startDate, endDate, userId, country, leaveRequestType} = data;
    const params = prepareQueryString({
      dateFrom: startDate,
      dateTo: endDate,
      userId,
      country,
      leaveRequestType: leaveRequestType
    });
    return Api<null, DaysCountRange>().get(`${process.env.REACT_APP_DAYS_API_URL}/api/v1/specialdays/count-enc-user?${params}`);
  },
  getSettings: function() {
    return Api<null, CalendarSettingsType>().get(`${process.env.REACT_APP_LM_API_URL}/${prefix}/settings/calendar/get`);
  },
  updateSettings: function(data: any) {
    return Api<null, CalendarSettingsType>().put(`${process.env.REACT_APP_LM_API_URL}/${prefix}/settings/calendar/update`, data);
  }
};

const countryService = {
  path: process.env.REACT_APP_LM_API_URL,
  getByLocation: function(){
    return Api<null,UnionLocations[]>().get(`${this.path}/${prefix}/country/list`);
  },
  getUserLocation: function(){
    return Api<null,UserLocationI>().get(`${this.path}/${prefix}/country/place`);
  }
};

const reportService = {
  path: process.env.REACT_APP_LM_API_URL,

  getFilters : function(){
    return Api<null, OrgReportSettings>().get(`${this.path}/${prefix}/settings/hr-report/get`);
  },
  updateFilters: function(payload: OrgReportSettingsUpdateDto){
    return Api<OrgReportSettingsUpdateDto, null>().put(`${this.path}/${prefix}/settings/hr-report/update`, payload);
  },
  getGeneralDivision: function(payload: SearchParamsT){
    return Api<SearchParamsT,GeneralDivision>().post(`${this.path}/${prefix}/report/department-general`, payload);
  },
  getSelectableDivisions: function(){
    return Api<null,SelectableDivisions>().get(`${this.path}/${prefix}/report/department`);
  },
  getDivisionsGrid: function(payload: SearchParamsT){
    return Api<SearchParamsT,DataGridI>().post(`${this.path}/${prefix}/report/department-grid`,payload);
  },
  fetchDepartmentReportState() {
    return Api<null, ReportDepStateDto>().get(`${this.path}/api/v1/settings/department-report/get`);

  },
  setDepartmentReportState(data: ReportDepStateDto) {
    return Api<ReportDepStateDto, null>().put(`${this.path}/api/v1/settings/department-report/update`, data);
  },
  downloadDepartmentReportData: function(payload: SelectableDivisionsListItem){
    return Api<SelectableDivisionsListItem, Response >().post(`${this.path}/${prefix}/report/department/reportdownload`, payload);
  },
  downloadSectorReportData: function(payload: SelectableDivisionsListItem){
    return Api<SelectableDivisionsListItem, Response >().post(`${this.path}/${prefix}/report/division/reportdownload`, payload);
  },
  downloadProjectReportData: function(isPendingRequestVisible: boolean, reportType: string, projectId: number, yearMonth: string) {
    const params = prepareQueryString({
      isPendingRequestVisible: isPendingRequestVisible.toString(),
      reportType,
      projectId,
      yearMonth,
    });
    return Api<null, Response>().get(`${this.path}/${prefix}/report/project/download/responce?${params}`);
  },
  getMemberHeaders: function() {
    return Api<null, Col[]>().get(`${this.path}/${prefix}/report/division-members-header`);
  },
  getMemberData: function(year: number){
    const params = prepareQueryString({
      yearFilter: year,
    });
    return Api<string,MembersDataI>().get(`${this.path}/${prefix}/report/division-members-data?${params}`);
  },
  getOrgReportTableData: function (toApi: ReportOrgFilters) {
    return Api<ReportOrgFilters,TableDataT>().post(`${process.env.REACT_APP_LM_API_URL}/${prefixV2}/report/organizational-structure-data`, toApi);
  },
  getOrgReportDataWithHrProperties: function(config: HrReportDto){
    return Api<HrReportDto,HrReportResponse>().post(`${process.env.REACT_APP_LM_API_URL}/${prefixV2}/report/hr-report`, config);
  },
  getOrgReportTableHeader: function() {
    return Api<null, Col[]>().get(`${process.env.REACT_APP_LM_API_URL}/${prefixV2}/report/organizational-structure-header`);
  },
  getOrgReportTimesheetData: function (dateToApi: string, userIds: number[]) {
    const params = prepareQueryString({
      startDate: dateToApi,
    });
    let url = `${process.env.REACT_APP_LM_API_URL}/${prefix}/timesheet/user?${params}`;
    for (const i of userIds) {
      const userIdParam = prepareQueryString({
        userId: i,
      });
      url += `&${userIdParam}`;
    }
    return Api<null, Response>().get(url);
  },
  getOrgReportTimesheetDataAll: function (payload: OrgReportPayload) {
    const url = `${process.env.REACT_APP_LM_API_URL}/${prefix}/timesheet/downloadall`;
    return Api<OrgReportPayload, Response >().post(url, payload);
  },
  getProjectList: function() {
    return Api<null, {
      list: Project[],
      selectedItem: Project,
      selectedFilters: {
        showPendingRequests: boolean
      }
    }>().get(`${process.env.REACT_APP_LM_API_URL}/${prefix}/report/projectsList`);
  },
  getPlannerListReport: function(plannerListId: number, date: string, isPendingRequestVisible: boolean){
    const params = prepareQueryString({
      plannerListId,
      yearMonth: date,
      isPendingRequestVisible: `${isPendingRequestVisible}`,
    });
    return Api<null, ProjectReportRes>().get(`${process.env.REACT_APP_LM_API_URL}/${prefix}/report/plannerList?${params}`);
  },
  getProjectManagerReport: function(projectId: number, date: string, isPendingRequestVisible: boolean){
    const params = prepareQueryString({
      projectId,
      yearMonth: date,
      isPendingRequestVisible: `${isPendingRequestVisible}`,
    });
    return Api<null,ProjectReportRes>().get(`${process.env.REACT_APP_LM_API_URL}/${prefix}/report/project?${params}`);
  },
  getCircles: function(projectId: number, date: string, isPendingRequestVisible: boolean){
    const params = prepareQueryString({
      circleTeamId: projectId,
      yearMonth: date,
      isPendingRequestVisible: `${isPendingRequestVisible}`,
    });
    return Api<null,ProjectReportRes>().get(`${process.env.REACT_APP_LM_API_URL}/${prefix}/report/circleTeam?${params}`);
  }
};

const leaveTypesService = {
  path: process.env.REACT_APP_LM_API_URL,
  getLeaveTypes: function(usedId?: string | number){
    const prepareUrl = usedId ? `?userId=${usedId}` : '';
    return Api<null, LeaveTypesI[]>().get(`${this.path}/${prefixV2}/leaverequest/types${prepareUrl}`);
  },
  getLeaveTypesByRequestId: function(id: string | number) {
    return Api<null, LeaveTypesI[]>().get(`${this.path}/${prefixV2}/leaverequest/types/${id}`);
  },
  getAllLeaveTypes: function() {
    return Api<null, LeaveTypesI[]>().get(`${this.path}/${prefixV2}/leaverequest/types/all`);
  },

  getLeavePrerequisites: function(userId?: number, date?: Date) {
    const params = prepareQueryString({
      userId,
      date: date ? dateToYMDFormat(date) : undefined
    });
    return Api<null, LeaveRequestPrerequisites>().get(`${this.path}/${prefix}/leaverequest/creation-prerequisites?${params}`);
  }
};

const requestService = {
  path: process.env.REACT_APP_LM_API_URL,
  getLeavesAll(year?: number, userId?: number){
    const params = prepareQueryString({
      year,
      userId,
    });
    return Api<null, LeavesAll[]>().get(`${this.path}/${prefix}/leaverequest/list-by-period?${params}`);
  },
  getSelectedLeaveRequest(id : number) {
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/leaverequest/${id}`;
    return Api<null, LeaveRequestId>().get(url);
  },
  getRequestList() {
    return Api<null, RequestsI[]>().get(`${process.env.REACT_APP_APPROVAL_API_URL}/api/v1/approval/approval-requests`);
  },
  approveRequest(id: number) {
    const params = prepareQueryString({
      id,
    });
    return Api<null, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/leaverequest/approve?${params}`);
  },
  rejectRequest(id: number, comment: string) {
    const params = prepareQueryString({
      comment,
      id
    });
    return Api<null, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/leaverequest/reject?${params}`);
  },
  cancelRequest(id: number, comment: string) {
    let url = `${process.env.REACT_APP_LM_API_URL}/api/v1/leaverequest/cancel/${id}`;
    if (comment !== '') {
      const params = prepareQueryString({
        comment,
      });
      url += ` ?${params}`;
    }
    return Api<null, null>().put(`${url}`);
  },
  cancelPlannedRequest(startDate: string, endDate: string) {
    return Api<null, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/leaverequest/planned/cancel?startDate=${startDate}&endDate=${endDate}`);
  },
  fetchAbsenceList(listName: string, listType: string, plannerListId?: number) {
    const params = prepareQueryString({
      listName,
      listType,
      plannerListId,
    });
    return Api<null, AbsenceI>().get(`${this.path}/api/v1/absenceList?${params}`);
  },
  fetchAbsenceListsForUser() {
    return Api<null, AbsenceListItemI[]>().get(`${this.path}/api/v1/settings/homepage/get`);
  },
  setAbsenceListsForUser(data: AbsenceListItemI[]) {
    return Api<AbsenceListItemI[], null>().put(`${this.path}/api/v1/settings/homepage/update`, data);
  },
  fetchAbsenceLists() {
    return Api<null,AbsentEmployeesT>().post(`${this.path}/api/v1/absenceLists`, null);
  },
  cancelRequestForEmployee(id: number, comment: string) {
    const params = prepareQueryString({
      comment,
    });
    return Api<null, LeaveRequestId>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/cancel-for-employee/${id}?${params}`);
  },
};

const absenceSettingsService = {
  path: process.env.REACT_APP_LM_API_URL,
  getAbsenceSettings() {
    return Api<null, AbsenceSettingsI>().get(`${this.path}/api/v1/settings/who-is-off/get`);
  },
  updateAbsenceSettings(body: AbsenceSettingsUpdateBodyI) {
    return Api<AbsenceSettingsUpdateBodyI, AbsenceSettingsI>().put(`${this.path}/api/v1/settings/who-is-off/update`, body);
  }
};

const authAccessService =  {
  path: process.env.REACT_APP_LM_API_URL,
  getMenuItems: function(){
    return Api<null,MenuItemsI[]>().get(`${this.path}/${prefix}/menu`);
  },
  getAccess: function(id: string){
    const params = prepareQueryString({
      menuItemId: id,
    });
    return Api<string,AccessItemI[]>().get(`${this.path}/${prefix}/accesses?${params}`);
  },
  getAllAccesses: function() {
    return Api<string,AccessItemI[]>().get(`${this.path}/${prefix}/user-accesses`);
  }
};

const daysAvailableService = {
  path: process.env.REACT_APP_LM_API_URL,
  updateUserBalance: function(dto: DaysAvailableNamespace.UpdateBalanceDto){
    return Api<DaysAvailableNamespace.UpdateBalanceDto, Response>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/employeeBalance/correction`, dto, false, true);
  },
  getEmployeeBalanceHistory: function(employeeId: number){
    return Api<null, DaysAvailableNamespace.BalanceHistoryCollection>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/history/employeeBalance?employeeId=${employeeId}`);
  },
  getRemainingDaysOldApi: function(ent?: {userId?: string, list?: number[], date?: Date}) {
    const prepareId = ent?.userId ? `&userId=${ent.userId}` : '';
    const prepareList = ent?.list ? `&excludedLeaveRequests=${ent.list}` : '';
    const prepareYear = ent?.date ? `&date=${dateToYMDFormat(ent.date)}` : '';
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/days?${prepareId}${prepareList}${prepareYear}`;
    return Api<null,RemaniningDays>().get(`${url}`);
  },
  getRemainingDay: function(ent?: {userId?: number, list?: number[]}) {
    const prepareId = ent?.userId ? `&userId=${ent.userId}` : '';
    const prepareList = ent?.list ? `&excludedLeaveRequests=${ent.list}` : '';
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/daysbalance?${prepareId}${prepareList}`;
    return Api<null,RemaniningDays>().get(`${url}`);
  },
  getRemainingDaysForUser: function(ent?: {userId?: number, list?: number[]}) {
    const prepareId = ent?.userId ? `&userId=${ent.userId}` : '';
    const prepareList = ent?.list ? `&excludedLeaveRequests=${ent.list}` : '';
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/daysbalance/available?${prepareId}${prepareList}`;
    return Api<null,RemaniningDays>().get(`${url}`);
  },
  editCompensatoryOff: function(data:EditCompensatoryOff) {
    const {balanceValue, employeeId, comment} = data;
    const params = prepareQueryString({
      balanceValue,
      employeeId,
      comment
    });
    return Api<null, Request>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/compensatoryoffbalance?${params}`, null);
  }
};

const requestForUserService = {
  path: process.env.REACT_APP_LM_API_URL,
  getAvailableUsers(query: string){
    const params = prepareQueryString({
      query,
    });
    const url = `${this.path}/api/v1/leaverequest/listEmployees?${params}`;
    return Api<null, UserList[]>().get(url);
  }
};

const helpService = {
  path: process.env.REACT_APP_LM_API_URL,
  getAvailableAttachments(countryName: string) {
    const params = prepareQueryString({
      countryName,
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/help/available-attachments?${params}`;
    return Api<null, LocaleAttachmentsType>().get(url);
  },
  getAvailableTemplates(countryName: string) {
    const params = prepareQueryString({
      countryName,
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/help/available-templates?${params}`;
    return Api<null, LocaleTemplateType>().get(url);
  },
  getDownloadFile(ref: string) {
    const url = `${process.env.REACT_APP_LM_API_URL}/${ref}`;
    return Api<null, Response>().get(url);
  },
  getAvailableTemplatesForUser(userId: number) {
    const params = prepareQueryString({
      userId,
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/help/available-templates-for-user?${params}`;
    return Api<null, LeaveTemplatesType>().get(url);
  }
};

const plannerService = {
  createPlanningAbsences: function(dateSet: string[]){
    return Api<string[], null>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/planning/create-absence`, dateSet);
  },
  deletePlanningAbsences: function(dateSet: string[]){
    return Api<string[], null>().delete(`${process.env.REACT_APP_LM_API_URL}/api/v1/planning/delete-absence`, dateSet);
  },
  getEmployeesEventsList: function(year: number, employeeIds: number[], plannerListType: string, projectName: string) {
    const params = prepareQueryString({
      year,
      employeeIds: employeeIds.join(','),
      plannerListType,
      projectName
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/planner/employee-events-and-status?${params}`;
    return Api<null, EmployeeEventsResponse[]>().get(url);
  },
  getEmployees: function (checkedIds:number[], value:string, page: number, pageSize:number ) {
    const params = prepareQueryString({
      checkedIds: checkedIds.join(','),
      name: value,
      page,
      perPage: pageSize,
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/employees/directory?${params}`;
    return Api<EmployeesPlanerI,any>().get(url);
  },
  createEmployeeList: function(data: Omit<PlannerListDTO, 'id' | 'plannerListType'>, isForWhoisOffToday?: boolean) {
    const queryForWhoisOffToday = (isForWhoisOffToday && isForWhoisOffToday === true) ? '?isForWhoisOffToday=true' : '';

    return Api<Omit<PlannerListDTO, 'id' | 'plannerListType'>, PlannerListDTO>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/planner/plannerlist${queryForWhoisOffToday}`, data, false);
  },
  getPlannerLists: function(collectStatistic?: boolean, isForWhoisOffToday?: boolean) {
    collectStatistic = isForWhoisOffToday;

    let queryUrl = '?isStatisticEnable=' + !!collectStatistic;
    
    if (isForWhoisOffToday) {
      queryUrl += '&isForWhoisOffToday=' + isForWhoisOffToday;
    }

    return Api<null, PlannerListMap>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/planner/plannerlists${queryUrl}`);
  },
  updatePlannerList: function(listId: number, userId: number){
    const params = prepareQueryString({
      listId,
      userId,
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/planner/plannerlist?${params}`;
    return Api<null, PlannerListDTO>().patch(url);
  },
  deletePlanerList: function(id:number) {
    return Api<null, PlannerListDTO[]>().delete(`${process.env.REACT_APP_LM_API_URL}/api/v1/planner/plannerlist/${id}`);
  },
  removeEmployee: function(listId: number, userIds: number[]) {
    const params = prepareQueryString({
      listId,
      userIds: userIds.join(','),
    });
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v1/planner/remove-employees?${params}`;
    return Api<null,null>().delete(url);
  },
};

const settingsService = {
  endpoint: 'settings',
  listCoefficients: function(){
    return Api<null,SettingsCoefficientType[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/dashboard/list-coefficients-by-country`);
  },
  employeesList: function (page: number, perPage: number, employeeName: string, order: 'ASC' | 'DESC') {
    const params = prepareQueryString({
      name: employeeName,
      order,
      page,
      perPage,
    });
    return Api<null, SettingsEmployeeListT>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/${this.endpoint}/employees/search?${params}`);
  },
  updateEmployeeList: function(data: UpdateEmployeeBody) {
    return Api<UpdateEmployeeBody, SettingsEmployeeListT>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/${this.endpoint}/employees/update`, data);
  },
  getPlannerFilters: function() {
    return Api<null, PlannerFilterDto>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/settings/plannerpage/get`);
  },
  addPlannerFilters: function(filters: PlannerFilterDto, collectStatistics?: boolean) {
    let queryUrl = '';
    if (collectStatistics) {
      queryUrl = '?isStatisticEnable=true';
    }
    return Api<PlannerFilterDto, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/settings/plannerpage/update${queryUrl}`, filters);
  },
  getSavedVacationSettings: function() {
    return Api<null, any>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/settings/vacationSettings/get`);
  },
  updateSavedVacationSettings: function(data: SavedSettingsType) {
    return Api<SavedSettingsType, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/settings/vacationSettings/update`, data);
  }
};

const profileService = {
  endpoint:'analytics',
  getAbsenceAnalytics: function(year: number, userId?: string  ) {
    const params = prepareQueryString({
      userId,
      year,
    });
    return Api<null, AnalyticsT>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/${this.endpoint}/statistics?${params}`);
  },
};

const supportService = {
  endpoint: 'support',
  submitFeedback : function (formData: FormData) {
    return Api<FormData, null> ().post(`${process.env.REACT_APP_SUPPORT_API_URL}/${prefix}/${this.endpoint}/create-lm-request`, formData, true);
  }
};

const templateService = {
  fetchTemplate: (data: DownloadTemplate) => {
    const params = prepareQueryString(data);
    return Api<null,Response>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/v2/download?${params}`);
  }
};

const extensionsService ={
  fetchAllowedExtensions : () => {
    const url = `${process.env.REACT_APP_LM_API_URL}/api/v2/leaverequest/types/all-supported-file-extensions`;
    return Api<null, string[]>().get(url);
  }
};

const userFiltersService = {
  getFilters : (pageName: FiltersPages) => {
    const params = prepareQueryString({
      page: pageName,
    });
    return Api<null, UserFilters>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/employees/get-filters?${params}`);
  },
  addFilters: (filters: UserFilters) => {
    return Api<UserFilters, UserFilters>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/employees/add-filters`, filters);
  }
};

const systemStateService = {
  getSystemState : () => {
    return Api<null, SystemState>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/technicalworks/nearesttechnicalworksdate`);
  },
  setShowNewsModal : (bool?:boolean) => {
    const params = prepareQueryString({
      iswelcomewindowwasviewed: `${bool || true}`,
    });
    return Api<null, null>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/employees/welcome-window-flag?${params}`);
  },
  checkActualityNewsModal : () => {
    return Api<null, boolean>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/employees/is-welcome-window-viewed`);
  },
};

const applicationTemplates = {
  getCountryList: () => {
    return Api<null, CountryList[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/available-countries`);
  },
  getAvailableTemplates: (countryName: UnionLocations) => {
    const params = prepareQueryString({
      countryName
    });
    return Api<null, ApplicationTemplatesGrid[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/list?${params}`);
  },
  getLeaveTypesByLocation: (countryName: UnionLocations) => {
    const params = prepareQueryString({
      countryName
    });
    return Api<null, TypesForTemplates[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/types?${params}`);
  },
  validateTemplate: (file: FormData) => {
    return Api<FormData, ApplicationTemplatesGrid>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/validate`, file, true );
  },
  downloadLocalFile: (file: FormData) => {
    return Api<FormData, Response>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/v1/download-original`, file, true );
  },
  createTemplate: (file: FormData) => {
    return Api<FormData, Response>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/create`, file, true, true );
  },
  updateTemplate: (file: FormData) => {
    return Api<FormData, Response>().put(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/update`, file, true, true );
  },
  deleteTemplate: (templateId: number) => {
    return Api<null, Response>().delete(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/delete/${templateId}`, undefined, undefined, true);
  },
  downloadTemplateFile: (ref: string) => {
    return Api<null, Response>().get(`${process.env.REACT_APP_LM_API_URL}/${ref}`);
  },
  obligatoryTemplatesByRequestType:(requestTypeId: number, userId?: number) => {
    const params = prepareQueryString({
      requestTypeId,
      userId,
    });
    return Api<null, ApplicationTemplatesGrid[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/template/obligatory-templates-by-requesttype?${params}`);
  }
};

const actingPerson = {
  getEmployeeList:(roleId: number, query: string) => {
    const params = prepareQueryString({
      query,
      roleId,
    });
    return Api<null, {employees: UserList[]}>().get(`${process.env.REACT_APP_LM_API_URL}/api/acting/v1/list-role-employees?${params}`);
  },
  getActingRoles:() => {
    return Api<null,ActingRole[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/acting/v1/list-user-roles`);
  },
  addActing: (role: ActingRole) => {
    return Api<ActingRole,ActingRole>().post(`${process.env.REACT_APP_LM_API_URL}/api/acting/v1/acting`, role);
  },
  updateActing: (role: ActingRole) => {
    const params = new URLSearchParams(role as unknown as Record<string, string>).toString();
    return Api<ActingRole,ActingRole>().put(`${process.env.REACT_APP_LM_API_URL}/api/acting/v1/acting?${params}`);
  },
  deleteActing: (id: number) => {
    return Api<ActingRole,ActingRole>().delete(`${process.env.REACT_APP_LM_API_URL}/api/acting/v1/acting/${id}`);
  },
};

const globalSearchService = {
  search: (query: string, path?: string) => {
    return Api<undefined,GlobalSearchCacheResult>().get(`${process.env.REACT_APP_LM_API_URL}/api/globalsearch/v1/employees?widgetName=${path}&query=${query}`);
  },
  addToRecentlySearch: (userId: number) => {
    return Api<undefined, GlobalSearchCacheResult>().post(`${process.env.REACT_APP_LM_API_URL}/api/globalsearch/v1/employee/save?userId=${userId}`, undefined);
  },
  recentlySearch: () => {
    return Api<undefined, GlobalSearchCacheResult>().get(`${process.env.REACT_APP_LM_API_URL}/api/globalsearch/v1/history`);
  },
  checkRelations: (userId: number) => {
    return Api<undefined, string[]>().get(`${process.env.REACT_APP_LM_API_URL}/api/globalsearch/v1/access-type-for-employee-profile?employeeId=${userId}`);
  }
};

const statisticsUsageService = {
  path: process.env.REACT_APP_LM_API_URL,
  helpPage: 'help-page-usage',
  calendarPage: 'calendar-page-usage',
  actingPersonPage: 'acting-page-usage',
  projectReportPage: 'project-report-page-usage',
  hrReportPage: 'hr-report-page-usage',
  departmentReportPage: 'department-report-page-usage',
  departmentReportGoToProfile: 'department-report-goto-profile',
  goToProfile: 'collect-search-profile',
  hrReportFilterDivisions: 'hr-report-filter-divisions',
  hrReportFilterLocations : 'hr-report-filter-locations',
  hrReportGoToProfile: 'hr-report-goto-profile',
  viewPlannedAbsence: 'view-planned-absence',
  absenceSettingsFakeClick: 'who-is-off/settings-click',
  sendUsageRequest: function(page: string) {
    return Api<null, null>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/analytics/${page}`, null);
  },
  sendUsageRequestWithBody: function(page: string, body: number[]) {
    return Api<number[], null>().post(`${process.env.REACT_APP_LM_API_URL}/api/v1/analytics/${page}`, body);
  },
  auditLogin : function(){
    return Api<null, null>().get(`${process.env.REACT_APP_LM_API_URL}/api/v2/analytics/post-login/`);
  }
};

const featureSwitcherService = {
  getFlags: function() {
    const url = `${process.env.REACT_APP_FEATURE_SWITCHER_URL}/public-api/featureFlags`;
    const body = {
      hashKey: `${process.env.REACT_APP_API_FEATURE_SWITCHER_PROJECT_HASH}`
    };

    return Api<any, any>().post(url, body);
  },

  getSelectors: function(variables: any) {
    const url = `${process.env.REACT_APP_FEATURE_SWITCHER_URL}/public-api/selectors`;
    const body = {
      hashKey: `${process.env.REACT_APP_API_FEATURE_SWITCHER_PROJECT_HASH}`,
      variables,
    };

    return Api<any, FeatureSwitcherSelectorsType>().post(url, body);
  }
};

const notificationService = {
  deleteNotification: function(id: number){
    return Api<null, null>().delete(`${process.env.REACT_APP_SUPPORT_API_URL}/api/v1/delete-notification?notificationId=${id}`);
  },
  deleteAllNotifications: function(ids: number[]){
    const toParams = ids.join(',');
    return Api<null, null>().delete(`${process.env.REACT_APP_SUPPORT_API_URL}/api/v1/delete-all-notifications?notificationIds=${toParams}`);
  }
};

const sysAdminPanelService = {
  getAvailableAPIs: function () {
    return Api<null, SysAdminAPIsResponse>().get(`${process.env.REACT_APP_LM_API_URL}/api/v1/sysadmin/allAPIs`);
  },
};


const apiConfig = {
  countryService,
  specialDaysService,
  reportService,
  leaveTypesService,
  authAccessService,
  daysAvailableService,
  helpService,
  calendarService,
  plannerService,
  requestService,
  absenceSettingsService,
  settingsService,
  profileService,
  templateService,
  extensionsService,
  supportService,
  userFiltersService,
  requestForUserService,
  systemStateService,
  applicationTemplates,
  actingPerson,
  globalSearchService,
  statisticsUsageService,
  featureSwitcherService,
  notificationService,
  sysAdminPanelService
};

export default apiConfig;
